import React, { useState, useRef, useCallback, useEffect } from 'react';
import Map, { Source, Layer, Marker } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Paper,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { makeApiCall } from '../../config/api';

// Area types with colors and default pricing
const AREA_TYPES = {
  lawn: { 
    label: 'Lawn', 
    color: '#4caf50', 
    fillColor: 'rgba(76, 175, 80, 0.3)',
    strokeColor: '#4caf50',
    defaultPrice: 0.15
  },
  driveway: { 
    label: 'Driveway', 
    color: '#9e9e9e', 
    fillColor: 'rgba(158, 158, 158, 0.3)',
    strokeColor: '#9e9e9e',
    defaultPrice: 0.25
  },
  cityWalkway: { 
    label: 'City Walkway', 
    color: '#607d8b', 
    fillColor: 'rgba(96, 125, 139, 0.3)',
    strokeColor: '#607d8b',
    defaultPrice: 0.20
  },
  internalWalkway: { 
    label: 'Internal Walkway', 
    color: '#78909c', 
    fillColor: 'rgba(120, 144, 156, 0.3)',
    strokeColor: '#78909c',
    defaultPrice: 0.20
  },
  garden: { 
    label: 'Garden', 
    color: '#ff9800', 
    fillColor: 'rgba(255, 152, 0, 0.3)',
    strokeColor: '#ff9800',
    defaultPrice: 0.18
  },
  patio: { 
    label: 'Patio', 
    color: '#795548', 
    fillColor: 'rgba(121, 85, 72, 0.3)',
    strokeColor: '#795548',
    defaultPrice: 0.30
  },
  snowPiling: {
    label: 'Snow Piling Area',
    color: '#ffeb3b',
    fillColor: 'rgba(255, 235, 59, 0.3)',
    strokeColor: '#fbc02d',
    defaultPrice: 0.00
  },
  other: { 
    label: 'Other', 
    color: '#9c27b0', 
    fillColor: 'rgba(156, 39, 176, 0.3)',
    strokeColor: '#9c27b0',
    defaultPrice: 0.15
  }
};

const SiteDiagramDrawer = ({ 
  open, 
  onClose, 
  onSave, 
  address, 
  quoteId,
  onValuesCalculated,
  existingAreaData = null
}) => {
  const mapRef = useRef(null);
  const [viewport, setViewport] = useState({
    latitude: 43.6532,
    longitude: -79.3832,
    zoom: 18
  });
  
  // Drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentAreaType, setCurrentAreaType] = useState('lawn');
  const [currentPolygon, setCurrentPolygon] = useState([]);
  const [drawnAreas, setDrawnAreas] = useState([]);
  const [editingArea, setEditingArea] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Load existing area data when dialog opens
  useEffect(() => {
    if (open && existingAreaData) {
      try {
        const data = typeof existingAreaData === 'string' 
          ? JSON.parse(existingAreaData) 
          : existingAreaData;
        
        if (data.areas && Array.isArray(data.areas)) {
          // Migrate old types and ensure validity
          const migratedAreas = data.areas.map(area => {
            if (area.type === 'sidewalk') return { ...area, type: 'cityWalkway' };
            // If type doesn't exist in current config, fallback to 'other'
            if (!AREA_TYPES[area.type]) return { ...area, type: 'other' };
            return area;
          });
          setDrawnAreas(migratedAreas);
        }
      } catch (err) {
        console.error('Error loading existing area data:', err);
      }
    }
  }, [open, existingAreaData]);

  // Geocoding for address
  useEffect(() => {
    if (address && open) {
      geocodeAddress(address);
    }
  }, [address, open]);

  const geocodeAddress = async (address) => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${process.env.REACT_APP_MAPBOX_TOKEN}`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const [lng, lat] = data.features[0].center;
        setViewport(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          zoom: 19
        }));
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      setError('Could not locate address. Please check the address and try again.');
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = () => {
    setIsDrawing(true);
    setCurrentPolygon([]);
    setEditingArea(null);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    if (currentPolygon.length >= 3) {
      const area = {
        id: Date.now(),
        type: currentAreaType,
        coordinates: [...currentPolygon],
        squareFeet: calculateArea(currentPolygon),
        pricePerSqFt: AREA_TYPES[currentAreaType].defaultPrice
      };
      setDrawnAreas(prev => [...prev, area]);
    }
    setCurrentPolygon([]);
  };

  const handleMapClick = (event) => {
    if (!isDrawing) return;
    
    const { lngLat } = event;
    const newPoint = [lngLat.lng, lngLat.lat];

    // Check if the new point is close enough to the starting point to close the polygon
    // Only check if we have at least 3 points already (to form a valid polygon)
    if (currentPolygon.length >= 3) {
      const startPoint = currentPolygon[0];
      
      // Calculate distance (in degrees - this is a rough approximation but fine for UI click detection)
      // A small threshold like 0.0001 degrees (approx 10 meters) should work for "clicking back on start"
      // Adjust threshold as needed based on map zoom/usability
      const dx = newPoint[0] - startPoint[0];
      const dy = newPoint[1] - startPoint[1];
      const distanceSquared = dx * dx + dy * dy;
      
      // Threshold for "clicking on the start point"
      // 0.0000001 is very small, might need tuning based on zoom level.
      // Let's use screen pixel distance instead if possible, but mapbox gl js gives lngLat.
      // 0.0001 deg is roughly 11 meters. At high zoom (house level), that might be too big if points are close.
      // Let's try to detect if they clicked on the marker itself.
      
      // Actually, a better way is to make the first marker clickable.
      // But if we stick to map click logic:
      
      // Let's just add the point. Closing logic will be handled by clicking the start marker.
    }
    
    setCurrentPolygon(prev => [...prev, newPoint]);
  };

  const handlePointClick = (index, event) => {
    event.originalEvent.stopPropagation(); // Prevent map click
    
    if (isDrawing && index === 0 && currentPolygon.length >= 3) {
      // If clicking the first point and we have enough points, close the polygon
      stopDrawing();
    }
  };

  const calculateArea = (coordinates) => {
    if (coordinates.length < 3) return 0;
    
    // Convert to meters using a simple approximation
    // This is a basic calculation - for production, you'd want more accurate geodetic calculations
    let area = 0;
    const n = coordinates.length;
    
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += coordinates[i][0] * coordinates[j][1];
      area -= coordinates[j][0] * coordinates[i][1];
    }
    
    area = Math.abs(area) / 2;
    
    // Convert from square degrees to square feet (rough approximation)
    // This is a simplified conversion - in production, use proper geodetic calculations
    const metersPerDegree = 111000; // Rough approximation
    const squareMeters = area * (metersPerDegree * metersPerDegree);
    const squareFeet = squareMeters * 10.764; // Convert square meters to square feet
    
    return Math.round(squareFeet);
  };

  const deleteArea = (areaId) => {
    setDrawnAreas(prev => prev.filter(area => area.id !== areaId));
  };

  const editArea = (area) => {
    setEditingArea(area);
    setCurrentAreaType(area.type);
  };

  const saveAreaEdit = () => {
    if (editingArea) {
      setDrawnAreas(prev => prev.map(area => 
        area.id === editingArea.id 
          ? { ...area, type: currentAreaType, pricePerSqFt: AREA_TYPES[currentAreaType].defaultPrice }
          : area
      ));
      setEditingArea(null);
    }
  };

  const getTotalSquareFeet = () => {
    return drawnAreas.reduce((total, area) => total + area.squareFeet, 0);
  };

  const getTotalCost = () => {
    return drawnAreas.reduce((total, area) => total + (area.squareFeet * area.pricePerSqFt), 0);
  };

  const getAreaBreakdown = () => {
    const breakdown = {};
    drawnAreas.forEach(area => {
      if (!breakdown[area.type]) {
        breakdown[area.type] = { squareFeet: 0, cost: 0 };
      }
      breakdown[area.type].squareFeet += area.squareFeet;
      breakdown[area.type].cost += area.squareFeet * area.pricePerSqFt;
    });
    return breakdown;
  };

  const handleSave = async () => {
    if (drawnAreas.length === 0) {
      setError('Please draw at least one area before saving.');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Create a canvas to render the map with drawn areas
      const canvas = await captureMapAsImage();
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob((blob) => {
          console.log('Canvas blob created:', blob);
          console.log('Blob size:', blob?.size, 'bytes');
          console.log('Blob type:', blob?.type);
          resolve(blob);
        }, 'image/png');
      });
      
      // Upload to S3
      const formData = new FormData();
      formData.append('site_diagram', blob, 'site_diagram_drawn.png');
      formData.append('quote_id', quoteId);
      formData.append('is_drawn', 'true');
      formData.append('area_data', JSON.stringify({
        areas: drawnAreas,
        totalSquareFeet: getTotalSquareFeet(),
        totalCost: getTotalCost(),
        breakdown: getAreaBreakdown()
      }));

      console.log('FormData created for upload');
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }

      const response = await makeApiCall('/api/files/upload/site-diagram', {
        method: 'POST',
        body: formData
      });

      console.log('Upload response status:', response.status);
      console.log('Upload response ok:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('Upload response data:', data);
        onSave(data);
        
        // Pass calculated values to parent
        if (onValuesCalculated) {
          onValuesCalculated({
            totalSquareFeet: getTotalSquareFeet(),
            totalCost: getTotalCost(),
            breakdown: getAreaBreakdown(),
            areas: drawnAreas
          });
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save site diagram');
      }
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save site diagram');
    } finally {
      setSaving(false);
    }
  };

  const captureMapAsImage = async () => {
    return new Promise((resolve, reject) => {
      try {
        const map = mapRef.current;
        if (!map) throw new Error('Map not available');

        // Get the actual Mapbox GL map instance
        const mapInstance = map.getMap ? map.getMap() : map;
        if (!mapInstance) throw new Error('Map instance not available');

        // Wait for map to be fully loaded and rendered
        const captureWithDelay = () => {
          setTimeout(() => {
            try {
              const mapCanvas = mapInstance.getCanvas();
              
              if (!mapCanvas) throw new Error('Map canvas not available');
              
              console.log('Map canvas dimensions:', mapCanvas.width, 'x', mapCanvas.height);
              console.log('Map canvas data URL length:', mapCanvas.toDataURL().length);
              
              // Create a new canvas with the same dimensions
              const canvas = document.createElement('canvas');
              canvas.width = mapCanvas.width;
              canvas.height = mapCanvas.height;
              const ctx = canvas.getContext('2d');

              // Draw the map directly
              ctx.drawImage(mapCanvas, 0, 0);

              console.log('Captured canvas dimensions:', canvas.width, 'x', canvas.height);
              console.log('Captured canvas data URL length:', canvas.toDataURL().length);

              resolve(canvas);
            } catch (error) {
              console.error('Error capturing canvas:', error);
              reject(error);
            }
          }, 500); // Wait 500ms for map to fully render
        };

        if (!mapInstance.loaded()) {
          mapInstance.once('idle', captureWithDelay);
        } else {
          captureWithDelay();
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleClose = () => {
    setDrawnAreas([]);
    setCurrentPolygon([]);
    setIsDrawing(false);
    setEditingArea(null);
    setError(null);
    onClose();
  };

  // Create GeoJSON for drawn areas
  const getAreasGeoJSON = () => {
    const features = drawnAreas.map(area => ({
      type: 'Feature',
      properties: {
        id: area.id,
        type: area.type,
        squareFeet: area.squareFeet,
        pricePerSqFt: area.pricePerSqFt
      },
      geometry: {
        type: 'Polygon',
        coordinates: [area.coordinates]
      }
    }));

    return {
      type: 'FeatureCollection',
      features
    };
  };

  // Create GeoJSON for current drawing polygon
  const getCurrentPolygonGeoJSON = () => {
    if (currentPolygon.length < 2) return null;

    return {
      type: 'Feature',
      properties: {
        type: currentAreaType
      },
      geometry: {
        type: 'LineString',
        coordinates: currentPolygon
      }
    };
  };

  const areasGeoJSON = getAreasGeoJSON();
  const currentPolygonGeoJSON = getCurrentPolygonGeoJSON();
  const breakdown = getAreaBreakdown();

  console.log('SiteDiagramDrawer render - open:', open, 'address:', address);

  // Check if address is complete
  const isAddressComplete = address && address.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Draw Site Diagram - {isAddressComplete ? address : 'No Address Selected'}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {!isAddressComplete ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100%',
            p: 4
          }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No Address Selected
            </Typography>
            <Typography variant="body2" color="textSecondary" align="center">
              Please select a customer with a complete address to draw a site diagram.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Map Section */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            <Map
              ref={mapRef}
              {...viewport}
              onMove={evt => setViewport(evt.viewState)}
              onClick={handleMapClick}
              mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/satellite-streets-v12"
              preserveDrawingBuffer={true}
            >
              {/* Drawn areas */}
              {areasGeoJSON.features.length > 0 && (
                <Source id="areas" type="geojson" data={areasGeoJSON}>
                  <Layer
                    id="areas-fill"
                    type="fill"
                    paint={{
                      'fill-color': [
                        'match',
                        ['get', 'type'],
                        'lawn', AREA_TYPES.lawn.color,
                        'driveway', AREA_TYPES.driveway.color,
                        'cityWalkway', AREA_TYPES.cityWalkway.color,
                        'internalWalkway', AREA_TYPES.internalWalkway.color,
                        'garden', AREA_TYPES.garden.color,
                        'patio', AREA_TYPES.patio.color,
                        'snowPiling', AREA_TYPES.snowPiling.color,
                        'other', AREA_TYPES.other.color,
                        '#888888'
                      ],
                      'fill-opacity': 0.4
                    }}
                  />
                  <Layer
                    id="areas-stroke"
                    type="line"
                    paint={{
                      'line-color': [
                        'match',
                        ['get', 'type'],
                        'lawn', AREA_TYPES.lawn.strokeColor,
                        'driveway', AREA_TYPES.driveway.strokeColor,
                        'cityWalkway', AREA_TYPES.cityWalkway.strokeColor,
                        'internalWalkway', AREA_TYPES.internalWalkway.strokeColor,
                        'garden', AREA_TYPES.garden.strokeColor,
                        'patio', AREA_TYPES.patio.strokeColor,
                        'snowPiling', AREA_TYPES.snowPiling.strokeColor,
                        'other', AREA_TYPES.other.strokeColor,
                        '#888888'
                      ],
                      'line-width': 2
                    }}
                  />
                </Source>
              )}

              {/* Current drawing polygon */}
              {currentPolygonGeoJSON && (
                <Source id="current-polygon" type="geojson" data={currentPolygonGeoJSON}>
                  <Layer
                    id="current-polygon-line"
                    type="line"
                    paint={{
                      'line-color': AREA_TYPES[currentAreaType].strokeColor,
                      'line-width': 3,
                      'line-dasharray': [2, 2]
                    }}
                  />
                </Source>
              )}

              {/* Markers for polygon points */}
              {currentPolygon.map((coord, index) => (
                <Marker 
                  key={index} 
                  longitude={coord[0]} 
                  latitude={coord[1]}
                  onClick={(e) => handlePointClick(index, e)}
                  style={{ cursor: (index === 0 && currentPolygon.length >= 3) ? 'pointer' : 'default' }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: index === 0 ? 'white' : AREA_TYPES[currentAreaType].strokeColor,
                      border: `2px solid ${AREA_TYPES[currentAreaType].strokeColor}`,
                      cursor: (index === 0 && currentPolygon.length >= 3) ? 'pointer' : 'default',
                      transform: (index === 0 && currentPolygon.length >= 3) ? 'scale(1.5)' : 'scale(1)',
                      transition: 'transform 0.2s',
                      '&:hover': {
                         transform: (index === 0 && currentPolygon.length >= 3) ? 'scale(1.8)' : 'scale(1)'
                      }
                    }}
                  />
                </Marker>
              ))}
            </Map>

            {/* Drawing controls overlay */}
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Drawing Tools
                </Typography>
                
                <FormControl size="small" sx={{ mb: 1, minWidth: 120 }}>
                  <InputLabel>Area Type</InputLabel>
                  <Select
                    value={currentAreaType}
                    onChange={(e) => setCurrentAreaType(e.target.value)}
                    disabled={isDrawing}
                  >
                    {Object.entries(AREA_TYPES).map(([key, config]) => (
                      <MenuItem key={key} value={key}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              backgroundColor: config.color,
                              borderRadius: 1
                            }}
                          />
                          {config.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box display="flex" gap={1}>
                  {!isDrawing ? (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={startDrawing}
                      startIcon={<AddIcon />}
                    >
                      Start Drawing
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      onClick={stopDrawing}
                      startIcon={<CheckIcon />}
                    >
                      Finish Area
                    </Button>
                  )}
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Sidebar with areas and calculations */}
          <Box sx={{ width: 350, p: 2, borderLeft: '1px solid #ddd', overflow: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              Drawn Areas
            </Typography>

            {drawnAreas.length === 0 ? (
              <Typography color="textSecondary">
                No areas drawn yet. Click "Start Drawing" to begin.
              </Typography>
            ) : (
              <Box>
                {drawnAreas.map((area) => (
                  <Paper key={area.id} sx={{ p: 2, mb: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Chip
                        label={AREA_TYPES[area.type].label}
                        sx={{
                          backgroundColor: AREA_TYPES[area.type].fillColor,
                          color: AREA_TYPES[area.type].strokeColor,
                          fontWeight: 'bold'
                        }}
                      />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => editArea(area)}
                          disabled={isDrawing}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deleteArea(area.id)}
                          disabled={isDrawing}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    
                    <Typography variant="body2">
                      <strong>Area:</strong> {area.squareFeet.toLocaleString()} sq ft
                    </Typography>
                    <Typography variant="body2">
                      <strong>Price:</strong> ${area.pricePerSqFt.toFixed(2)}/sq ft
                    </Typography>
                    <Typography variant="body2">
                      <strong>Total:</strong> ${(area.squareFeet * area.pricePerSqFt).toFixed(2)}
                    </Typography>
                  </Paper>
                ))}

                <Divider sx={{ my: 2 }} />

                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>

                {Object.entries(breakdown).map(([type, data]) => (
                  <Box key={type} display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="body2">
                      {AREA_TYPES[type].label}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {data.squareFeet.toLocaleString()} sq ft
                    </Typography>
                  </Box>
                ))}

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body1" fontWeight="bold">
                    Total Area:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {getTotalSquareFeet().toLocaleString()} sq ft
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body1" fontWeight="bold">
                    Total Cost:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    ${getTotalCost().toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        {isAddressComplete && (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={drawnAreas.length === 0 || saving}
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {saving ? 'Saving...' : 'Save Site Diagram'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SiteDiagramDrawer;
